import React, { useState, useEffect } from 'react';
import { Edit3, Trash2, Home, FileText, Folder, Copy, Search, X, LayoutGrid, Clock, Calendar, Cloud, HardDrive, Filter, AlertTriangle, Wifi, WifiOff, Download, Check, RefreshCw, FileX, ClipboardCheck, Timer, AlertCircle } from 'lucide-react';
import { getFileExpirationInfo, formatTimeRemaining, getExpirationColorClass, getExpirationIconColor, getProgressBarColor, getExpiredFiles } from '../utils/fileExpiration';
import type { CurrentPage } from '../App';
import { Filesystem, Directory, ReaddirResult } from '@capacitor/filesystem';
import type { FileInfo } from '@capacitor/filesystem';
import { 
  getAllFloorPlansFromSupabase, 
  getAllInventoriesFromSupabase,
  getAllRetrofitSurveysFromSupabase,
  deleteFloorPlanFromSupabase, 
  deleteInventoryFromSupabase,
  deleteRetrofitSurveyFromSupabase,
  testSupabaseConnection,
  type FloorPlanListItem,
  type InventoryListItem,
  type RetrofitSurveyListItem,
  loadFloorPlanFromSupabase,
  saveFloorPlanToSupabase
} from '../utils/supabaseStorage';
import { 
  loadRetrofitSurveyFromSupabase, 
  saveRetrofitSurveyToSupabase,
  type RetrofitSurveyData
} from '../utils/retrofitSurveyStorage';
import { loadFloorPlanFromFile, type SavedFloorPlanData } from '../utils/fileUtils';
import AddressInputModal from './AddressInputModal';
import OrientationModal from './OrientationModal';
import FileCleanupModal from './FileCleanupModal';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useSyncQueue } from '../hooks/useSyncQueue';
import OfflineStorageService, { type OfflineFileMetadata } from '../services/offlineStorage';
import { getOrganizationMembers, canViewAllFiles } from '../services/teamService';
import type { OrganizationMember } from '../types/team';
import { supabase } from '../lib/supabaseClient';

interface MyFilesPageProps {
  onNavigate: (page: CurrentPage, id?: string) => void;
}

interface FileItem {
  id: string;
  name: string;
  type: 'floorplan' | 'inventory' | 'retrofit-survey';
  planType?: 'energy' | 'estate'; // Only for floorplans (energy=FloorPlanCreator, estate=Estate_floorplan)
  address?: string; // Only for retrofit surveys
  isLocal: boolean;
  createdAt?: string;
  updatedAt?: string;
  isAvailableOffline?: boolean;
  syncStatus?: 'synced' | 'pending' | 'conflict' | 'local-only';
  cloudId?: string;
  lastSyncedAt?: string;
  userId?: string; // Owner of the file
  userEmail?: string; // Owner's email
  userFirstName?: string; // Owner's first name
}

// Helper function to format file names for display by converting underscores to spaces
// and handling potential formatting issues
const formatFileNameForDisplay = (fileName: string): string => {
  if (!fileName) return 'Untitled';
  
  return fileName
    .replace(/^retrofit-/i, '')  // Remove 'retrofit-' prefix (case insensitive)
    .replace(/_/g, ' ')           // Convert underscores to spaces
    .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
    .trim();                     // Remove leading/trailing spaces
};

interface DuplicationState {
  isOpen: boolean;
  file: FileItem | null;
  step: 'address' | 'orientation' | 'saving';
  newAddress: string;
  newOrientation: number;
  originalData: SavedFloorPlanData | RetrofitSurveyData | null;
}

// Directory constants for local storage
const FLOOR_PLANS_DIR = 'floorplans';
const INVENTORIES_DIR = 'inventories';

const MyFilesPage: React.FC<MyFilesPageProps> = ({ onNavigate }) => {
  // Scroll to top when page loads (backup - App.tsx also handles this)
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [allFiles, setAllFiles] = useState<FileItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'energy' | 'estate' | 'inventory' | 'retrofit-survey'>('all');
  const [filterUser, setFilterUser] = useState<string>('all'); // 'all' or user_id
  const [teamMembers, setTeamMembers] = useState<OrganizationMember[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCloudConnected, setIsCloudConnected] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [duplicationState, setDuplicationState] = useState<DuplicationState>({
    isOpen: false,
    file: null,
    step: 'address',
    newAddress: '',
    newOrientation: 0,
    originalData: null
  });
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [isOpeningFile, setIsOpeningFile] = useState(false);

  // Offline functionality hooks
  const { status: networkStatus, forceCheck, isChecking } = useNetworkStatus();
  const { syncStatus, queueItems, forceSyncFile } = useSyncQueue();
  const offlineService = OfflineStorageService.getInstance();

  // Scroll to top on mount to ensure page starts from top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Enhanced filter files based on search term and unified filter
  const filteredFiles = allFiles.filter(file => {
    const displayName = formatFileNameForDisplay(file.name);
    const fileType = file.type === 'floorplan' ? 'Plan' : 
                     file.type === 'inventory' ? 'Inventory' : 
                     'Retrofit Survey';
    const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           fileType.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (file.userFirstName && file.userFirstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (file.userEmail && file.userEmail.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Type filter logic
    let matchesType = true;
    if (filterType === 'energy') matchesType = file.type === 'floorplan' && (!file.planType || file.planType === 'energy');
    else if (filterType === 'estate') matchesType = file.type === 'floorplan' && file.planType === 'estate';
    else if (filterType === 'inventory') matchesType = file.type === 'inventory';
    else if (filterType === 'retrofit-survey') matchesType = file.type === 'retrofit-survey';
    
    // User filter logic (for admins only)
    const matchesUser = filterUser === 'all' || file.userId === filterUser;
    
    return matchesSearch && matchesType && matchesUser;
  });

  // Get file counts for different types
  const energyPlans = allFiles.filter(f => f.type === 'floorplan' && (!f.planType || f.planType === 'energy'));
  const estatePlans = allFiles.filter(f => f.type === 'floorplan' && f.planType === 'estate');
  
  const fileCounts = {
    all: allFiles.length,
    energy: energyPlans.length,
    estate: estatePlans.length,
    inventory: allFiles.filter(f => f.type === 'inventory').length,
    'retrofit-survey': allFiles.filter(f => f.type === 'retrofit-survey').length,
    local: allFiles.filter(f => f.isLocal).length,
    cloud: allFiles.filter(f => !f.isLocal).length,
    syncing: allFiles.filter(f => f.syncStatus === 'pending').length
  };
  
  // Debug log file counts
  console.log('[MyFilesPage] File counts:', {
    total: fileCounts.all,
    energy: fileCounts.energy,
    estate: fileCounts.estate,
    estatePlanNames: estatePlans.map(p => p.name),
    inventory: fileCounts.inventory,
    retrofitSurvey: fileCounts['retrofit-survey']
  });

  // Clear search and filters
  const clearSearch = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterUser('all');
  };

  // Multi-select functions
  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  const selectAllFiles = () => {
    setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
  };

  const clearSelection = () => {
    setSelectedFiles(new Set());
    setIsMultiSelectMode(false);
  };

  const handleBulkDelete = async () => {
    const selectedFileObjects = filteredFiles.filter(f => selectedFiles.has(f.id));
    
    if (selectedFileObjects.length === 0) return;
    
    const fileNames = selectedFileObjects.map(f => formatFileNameForDisplay(f.name)).join(', ');
    
    if (!window.confirm(`Are you sure you want to delete ${selectedFileObjects.length} files?\n\n${fileNames}\n\nThis cannot be undone.`)) {
      return;
    }
    
    // Delete all files in parallel using Promise.allSettled
    const deletePromises = selectedFileObjects.map(file => 
      handleDeleteFile(file, true).catch(error => {
        console.error(`Failed to delete ${file.name}:`, error);
        return { success: false, error, file };
      })
    );
    
    const results = await Promise.allSettled(deletePromises);
    
    // Log any failures
    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      console.error(`${failures.length} files failed to delete:`, failures);
    }
    
    // Clear selection and refresh
    clearSelection();
    await loadFiles();
  };

  // Get file type icon
  const getFileTypeIcon = (type: 'floorplan' | 'inventory' | 'retrofit-survey') => {
    switch (type) {
      case 'floorplan':
        return LayoutGrid;
      case 'inventory':
        return FileText;
      case 'retrofit-survey':
        return ClipboardCheck;
      default:
        return FileText;
    }
  };

  // Get relative time for last modified
  const getRelativeTime = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString();
  };

  const loadFiles = async () => {
    // 🎯 PERFORMANCE TRACKING: Start overall timer
    const perfStart = performance.now();
    console.log('⏱️ [PERF] MyFiles page load started');
    
    setIsLoadingFiles(true);
    setErrorMessage(null);
    
    // Test Supabase connection first
    const connStart = performance.now();
    console.log('[MyFilesPage] Testing Supabase connection...');
    const connectionTest = await testSupabaseConnection();
    const connTime = performance.now() - connStart;
    console.log(`⏱️ [PERF] Supabase connection test: ${connTime.toFixed(2)}ms (Status: ${connectionTest.connected ? 'Connected' : 'Failed'})`);
    
    setIsCloudConnected(connectionTest.connected);
    setConnectionError(connectionTest.error || null);
    
    if (!connectionTest.connected) {
      console.warn('[MyFilesPage] Cloud storage not available:', connectionTest.error);
      setErrorMessage(`Could not connect to cloud storage: ${connectionTest.error}. Showing offline files only.`);
    }

    let combinedFiles: FileItem[] = [];
    
    // 🔧 FIX: Declare timing variables at function scope for final summary
    let supabaseTime = 0;
    let fpProcessTime = 0;
    let invProcessTime = 0;
    let surveyProcessTime = 0;
    
    // 🎯 PERFORMANCE: Track offline files retrieval
    const offlineStart = performance.now();
    const offlineFiles = offlineService.getOfflineFiles();
    const offlineTime = performance.now() - offlineStart;
    console.log(`⏱️ [PERF] Offline files retrieval: ${offlineTime.toFixed(2)}ms (${offlineFiles.length} files)`);

    // Load from Supabase if connected
    if (connectionTest.connected) {
      try {
        // 🎯 PERFORMANCE: Track Supabase data fetch
        const supabaseStart = performance.now();
        console.log('[MyFilesPage] Loading files from Supabase...');
        const [supabaseFloorPlans, supabaseInventories, supabaseRetrofitSurveys] = await Promise.all([
          getAllFloorPlansFromSupabase(),
          getAllInventoriesFromSupabase(),
          getAllRetrofitSurveysFromSupabase()
        ]);
        supabaseTime = performance.now() - supabaseStart;
        console.log(`⏱️ [PERF] Supabase data fetch: ${supabaseTime.toFixed(2)}ms (${supabaseFloorPlans.length} plans, ${supabaseInventories.length} inventories, ${supabaseRetrofitSurveys.length} surveys)`);

        // 🎯 PERFORMANCE: Track floor plans processing
        const fpProcessStart = performance.now();
        // Add Supabase floor plans
        for (const fp of supabaseFloorPlans) {
          const offlineFile = offlineFiles.find(of => of.cloudId === fp.id);
          
          // Check if this is a problematic file (numeric name indicates orphaned record)
          const isOrphanedRecord = /^\d+$/.test(fp.name);
          
          if (isOrphanedRecord) {
            console.warn(`[MyFilesPage] 🔴 ORPHANED FLOOR PLAN FOUND: ID=${fp.id}, Name="${fp.name}"`);
          }
          
          if (isOrphanedRecord) {
            console.log(`[MyFilesPage] 🔴 Loading ORPHANED floor plan from SUPABASE: ID=${fp.id}, Name="${fp.name}"`);
          }
          
          // Normalize plan_type: only 'energy' or 'estate' are valid, everything else defaults to 'energy'
          const normalizedPlanType = fp.plan_type === 'estate' ? 'estate' : 'energy';
          
          // Debug log for estate plans
          if (fp.plan_type === 'estate') {
            console.log(`[MyFilesPage] 🏢 Estate plan loaded from DB:`, {
              id: fp.id,
              name: fp.name,
              originalPlanType: fp.plan_type,
              normalizedPlanType
            });
          }
          
          combinedFiles.push({
            id: fp.id,
            name: fp.name,
            type: 'floorplan',
            planType: normalizedPlanType,
            isLocal: false,
            createdAt: fp.created_at,
            updatedAt: fp.updated_at,
            ...enrichFileWithUserData(fp.user_id),
            isAvailableOffline: offlineFile?.isAvailableOffline || false,
            syncStatus: isOrphanedRecord ? 'local-only' : (offlineFile?.syncStatus || 'synced'),
            cloudId: fp.id,
            lastSyncedAt: offlineFile?.lastSyncedAt
          });
        }
        fpProcessTime = performance.now() - fpProcessStart;
        console.log(`⏱️ [PERF] Floor plans processing: ${fpProcessTime.toFixed(2)}ms (${supabaseFloorPlans.length} plans)`);

        // 🎯 PERFORMANCE: Track inventories processing
        const invProcessStart = performance.now();
        // Add Supabase inventories
        for (const inv of supabaseInventories) {
          const offlineFile = offlineFiles.find(of => of.cloudId === inv.id);
          
          // Check if this is a problematic file (numeric name indicates orphaned record)
          const isOrphanedRecord = /^\d+$/.test(inv.name);
          
          if (isOrphanedRecord) {
            console.warn(`[MyFilesPage] Found potential orphaned inventory record: ID=${inv.id}, Name="${inv.name}"`);
          }
          
          combinedFiles.push({
            id: inv.id,
            name: inv.name,
            type: 'inventory',
            isLocal: false,
            createdAt: inv.created_at,
            updatedAt: inv.updated_at,
            ...enrichFileWithUserData(inv.user_id),
            isAvailableOffline: offlineFile?.isAvailableOffline || false,
            syncStatus: isOrphanedRecord ? 'local-only' : (offlineFile?.syncStatus || 'synced'),
            cloudId: inv.id,
            lastSyncedAt: offlineFile?.lastSyncedAt
          });
        }
        invProcessTime = performance.now() - invProcessStart;
        console.log(`⏱️ [PERF] Inventories processing: ${invProcessTime.toFixed(2)}ms (${supabaseInventories.length} inventories)`);

        // 🎯 PERFORMANCE: Track retrofit surveys processing
        const surveyProcessStart = performance.now();
        // Add Supabase retrofit surveys
        for (const survey of supabaseRetrofitSurveys) {
          const offlineFile = offlineFiles.find(of => of.cloudId === survey.id);
          
          // Check if this is a problematic file (numeric name indicates orphaned record)
          const isOrphanedRecord = /^\d+$/.test(survey.name);
          
          if (isOrphanedRecord) {
            console.warn(`[MyFilesPage] Found potential orphaned retrofit survey record: ID=${survey.id}, Name="${survey.name}"`);
          }
          
          combinedFiles.push({
            id: survey.id,
            name: survey.name,
            type: 'retrofit-survey',
            address: survey.address,
            isLocal: false,
            createdAt: survey.created_at,
            updatedAt: survey.updated_at,
            ...enrichFileWithUserData(survey.user_id),
            isAvailableOffline: offlineFile?.isAvailableOffline || false,
            syncStatus: isOrphanedRecord ? 'local-only' : (offlineFile?.syncStatus || 'synced'),
            cloudId: survey.id,
            lastSyncedAt: offlineFile?.lastSyncedAt
          });
        }
        surveyProcessTime = performance.now() - surveyProcessStart;
        console.log(`⏱️ [PERF] Retrofit surveys processing: ${surveyProcessTime.toFixed(2)}ms (${supabaseRetrofitSurveys.length} surveys)`);

        console.log(`[MyFilesPage] Loaded ${supabaseFloorPlans.length} floor plans, ${supabaseInventories.length} inventories, and ${supabaseRetrofitSurveys.length} retrofit surveys from Supabase`);
      } catch (error) {
        console.error('[MyFilesPage] Error loading files from Supabase:', error);
        setErrorMessage(`Error loading cloud files: ${(error as Error).message}. Showing offline files only.`);
        setIsCloudConnected(false);
      }
    }

    // 🎯 PERFORMANCE: Track offline-only files processing
    const offlineOnlyStart = performance.now();
    // Add offline-only files (files that exist only locally)
    offlineFiles.forEach((offlineFile: OfflineFileMetadata) => {
      // Skip if already added from cloud
      if (combinedFiles.some(f => f.id === offlineFile.cloudId || f.id === offlineFile.id)) {
        return;
      }
      
      const isOrphanedOffline = /^\d+$/.test(offlineFile.name);
      if (isOrphanedOffline) {
        console.log(`[MyFilesPage] 🔴 Loading ORPHANED OFFLINE FILE: Name="${offlineFile.name}", ID=${offlineFile.id}`);
      }
      
      // Normalize planType for offline floor plans
      const offlinePlanType = offlineFile.type === 'floorplan' 
        ? (offlineFile.planType === 'estate' ? 'estate' : 'energy')
        : undefined;
      
      combinedFiles.push({
        id: offlineFile.id,
        name: offlineFile.name,
        type: offlineFile.type,
        planType: offlinePlanType,
        isLocal: true,
        createdAt: offlineFile.lastModifiedAt,
        updatedAt: offlineFile.lastModifiedAt,
        isAvailableOffline: offlineFile.isAvailableOffline,
        syncStatus: offlineFile.syncStatus,
        cloudId: offlineFile.cloudId,
        lastSyncedAt: offlineFile.lastSyncedAt
      });
    });
    const offlineOnlyTime = performance.now() - offlineOnlyStart;
    const offlineOnlyCount = combinedFiles.filter(f => f.isLocal && !f.cloudId).length;
    console.log(`⏱️ [PERF] Offline-only files processing: ${offlineOnlyTime.toFixed(2)}ms (${offlineOnlyCount} local files)`);

    // 🎯 PERFORMANCE: Track legacy local files loading
    const legacyStart = performance.now();
    // Load legacy local files as fallback
    try {
      console.log('[MyFilesPage] Loading legacy local files...');
      const directories = ['floorplans', 'inventories'];
      
      for (const dir of directories) {
        try {
          // First check if directory exists, create if it doesn't
          try {
            await Filesystem.stat({
              path: dir,
              directory: Directory.Documents
            });
          } catch (statError) {
            // Directory doesn't exist, create it
            console.log(`[MyFilesPage] Creating ${dir} directory...`);
            await Filesystem.mkdir({
              path: dir,
              directory: Directory.Documents,
              recursive: false
            });
          }

          // Now try to read the directory
          const result: ReaddirResult = await Filesystem.readdir({
            path: dir,
            directory: Directory.Documents
          });

          result.files.forEach((file: FileInfo) => {
            if (file.name.endsWith('.json')) {
              const nameWithoutExtension = file.name.replace('.json', '');
              // Only add if not already present from Supabase or offline service
              const alreadyExists = combinedFiles.some(f => 
                f.name === nameWithoutExtension && 
                f.type === (dir === 'floorplans' ? 'floorplan' : 'inventory')
              );
              if (!alreadyExists) {
                const isOrphanedLocal = /^\d+$/.test(nameWithoutExtension);
                if (isOrphanedLocal) {
                  console.log(`[MyFilesPage] 🔴 Loading ORPHANED LOCAL FILE: Name="${nameWithoutExtension}", File="${file.name}"`);
                }
                combinedFiles.push({
                  id: file.name, // Use filename as ID for local files
                  name: nameWithoutExtension,
                  type: dir === 'floorplans' ? 'floorplan' : 'inventory',
                  isLocal: true,
                  createdAt: file.ctime ? new Date(file.ctime).toISOString() : undefined,
                  updatedAt: file.mtime ? new Date(file.mtime).toISOString() : undefined,
                  isAvailableOffline: false,
                  syncStatus: 'local-only'
                });
                              }
            }
          });
        } catch (dirError) {
          console.warn(`[MyFilesPage] Could not read ${dir} directory:`, dirError);
        }
      }

      const legacyTime = performance.now() - legacyStart;
      const legacyCount = combinedFiles.length - offlineOnlyCount - (connectionTest.connected ? 1 : 0);
      console.log(`⏱️ [PERF] Legacy local files loading: ${legacyTime.toFixed(2)}ms`);
      console.log(`[MyFilesPage] Total files loaded: ${combinedFiles.length}`);
    } catch (error) {
      const legacyTime = performance.now() - legacyStart;
      console.log(`⏱️ [PERF] Legacy local files loading (with errors): ${legacyTime.toFixed(2)}ms`);
      console.error('[MyFilesPage] Error loading local files:', error);
      if (!connectionTest.connected) {
        setErrorMessage('Could not load files from cloud or local storage.');
      }
    }

    // 🎯 PERFORMANCE: Track sorting
    const sortStart = performance.now();
    // Sort files by updated date (newest first)
    combinedFiles.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });
    const sortTime = performance.now() - sortStart;
    console.log(`⏱️ [PERF] File sorting: ${sortTime.toFixed(2)}ms (${combinedFiles.length} files)`);

    // 🗑️ AUTO-DELETE: Check for and delete expired files (older than 1 year)
    const expiredFiles = getExpiredFiles(combinedFiles);
    if (expiredFiles.length > 0) {
      console.log(`🗑️ [AUTO-DELETE] Found ${expiredFiles.length} expired files (older than 1 year)`);
      
      // Delete expired files in the background
      for (const expiredFile of expiredFiles) {
        try {
          console.log(`🗑️ [AUTO-DELETE] Deleting expired file: ${expiredFile.name}`);
          await handleDeleteFile(expiredFile, true); // Skip confirmation for auto-delete
        } catch (error) {
          console.error(`🗑️ [AUTO-DELETE] Failed to delete ${expiredFile.name}:`, error);
        }
      }
      
      // Filter out expired files from the list
      combinedFiles = combinedFiles.filter(f => !expiredFiles.includes(f));
      console.log(`🗑️ [AUTO-DELETE] Removed ${expiredFiles.length} expired files. ${combinedFiles.length} files remaining.`);
    }

    setAllFiles(combinedFiles);
    setIsLoadingFiles(false);
    
    // 🎯 PERFORMANCE: Log total time and breakdown
    const perfTotal = performance.now() - perfStart;
    console.log(`⏱️ [PERF] ===== MyFiles Page Load Complete =====`);
    console.log(`⏱️ [PERF] Total load time: ${perfTotal.toFixed(2)}ms`);
    console.log(`⏱️ [PERF] Breakdown:`);
    console.log(`⏱️ [PERF]   - Connection test: ${connTime.toFixed(2)}ms (${((connTime/perfTotal)*100).toFixed(1)}%)`);
    console.log(`⏱️ [PERF]   - Offline files: ${offlineTime.toFixed(2)}ms (${((offlineTime/perfTotal)*100).toFixed(1)}%)`);
    if (connectionTest.connected) {
      const supabaseTotal = (supabaseTime || 0) + (fpProcessTime || 0) + (invProcessTime || 0) + (surveyProcessTime || 0);
      console.log(`⏱️ [PERF]   - Supabase total: ${supabaseTotal.toFixed(2)}ms (${((supabaseTotal/perfTotal)*100).toFixed(1)}%)`);
    }
    console.log(`⏱️ [PERF]   - Sorting: ${sortTime.toFixed(2)}ms (${((sortTime/perfTotal)*100).toFixed(1)}%)`);
    console.log(`⏱️ [PERF] Files loaded: ${combinedFiles.length} total (${combinedFiles.filter(f => !f.isLocal).length} cloud, ${combinedFiles.filter(f => f.isLocal).length} local)`);
    console.log(`⏱️ [PERF] ========================================`);
  };

  // Helper to enrich file with user data from team members
  const enrichFileWithUserData = (fileUserId?: string): { userId?: string; userEmail?: string; userFirstName?: string } => {
    if (!fileUserId) return {};
    
    const member = teamMembers.find(m => m.user_id === fileUserId);
    if (member) {
      return {
        userId: fileUserId,
        userEmail: member.email,
        userFirstName: member.user_metadata?.first_name || member.email?.split('@')[0] || 'Unknown'
      };
    }
    
    return { userId: fileUserId };
  };

  // Load team members and check if user can view all files (super_admin OR team_leader)
  useEffect(() => {
    const loadTeamData = async () => {
      try {
        const [canViewAll, members] = await Promise.all([
          canViewAllFiles(), // Checks both super_admin AND team_leader
          getOrganizationMembers()
        ]);
        setIsAdmin(canViewAll); // Both super_admin and team_leader can see all files
        setTeamMembers(members);
        console.log('[MyFilesPage] 👥 Loaded team data:', { canViewAllFiles: canViewAll, memberCount: members.length });
      } catch (error) {
        console.error('[MyFilesPage] Error loading team data:', error);
      }
    };
    loadTeamData();
  }, []);

  useEffect(() => {
    console.log('🚀 [MyFilesPage] Component mounted - initiating file load');
    loadFiles();
  }, []);

  // Handle offline availability toggle
  const handleToggleOfflineAvailability = async (file: FileItem, available: boolean) => {
    try {
      await offlineService.toggleOfflineAvailability(file.id, available);
      // Refresh the file list to show updated offline status
      await loadFiles();
    } catch (error) {
      console.error('[MyFilesPage] Error toggling offline availability:', error);
      setErrorMessage(`Failed to ${available ? 'enable' : 'disable'} offline availability: ${(error as Error).message}`);
    }
  };

  // Handle force sync
  const handleForceSync = async (file: FileItem) => {
    try {
      await forceSyncFile(file.id);
      // Refresh the file list to show updated sync status
      await loadFiles();
    } catch (error) {
      console.error('[MyFilesPage] Error forcing sync:', error);
      setErrorMessage(`Failed to sync file: ${(error as Error).message}`);
    }
  };

  const handleOpenFile = (file: FileItem) => {
    // Show loading overlay
    setIsOpeningFile(true);
    
    // Track when loading started
    const loadingStartTime = Date.now();
    const MIN_LOADING_TIME = 1500; // 1.5 seconds minimum display
    
    // Small delay to ensure loading overlay renders before navigation
    setTimeout(() => {
      if (file.type === 'floorplan') {
        // Route based on plan_type
        if (file.planType === 'estate') {
          // Estate plans use Estate_floorplan component
          onNavigate({ page: 'retrofit/floorplan', floorPlanId: file.id });
        } else {
          // Energy plans (or undefined planType for backwards compatibility) use FloorPlanCreator
          onNavigate({ page: 'floorPlan', floorPlanId: file.id });
        }
      } else if (file.type === 'inventory') {
        onNavigate({ page: 'propertyInventory', inventoryId: file.id });
      } else if (file.type === 'retrofit-survey') {
        onNavigate({ page: 'retrofit/survey', floorPlanId: file.id });
      }
      
      // Ensure loading screen shows for minimum time
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      setTimeout(() => {
        setIsOpeningFile(false);
      }, remainingTime);
    }, 100);
  };

  const handleDeleteFile = async (file: FileItem, skipConfirmation: boolean = false) => {
    console.log(`[Delete] 🚀 handleDeleteFile called with:`, file);
    
    const friendlyFileType = file.type === 'floorplan' ? 'floor plan' : 
                           file.type === 'inventory' ? 'inventory report' : 
                           'retrofit survey';
    const displayName = formatFileNameForDisplay(file.name);

    console.log(`[Delete] 📋 Confirmation dialog for: "${displayName}"`);
    
    if (!skipConfirmation && !window.confirm(`Are you sure you want to delete the ${friendlyFileType} "${displayName}"? This cannot be undone.`)) {
      console.log(`[Delete] ❌ User cancelled deletion`);
      return;
    }
    
    console.log(`[Delete] ✅ User confirmed deletion, proceeding...`);

    try {
      let deleteSuccess = false;
      const errors: string[] = [];

      console.log(`[Delete] Attempting to delete file:`, {
        id: file.id,
        name: file.name,
        type: file.type,
        isLocal: file.isLocal,
        cloudId: file.cloudId
      });

      // ENHANCED DELETION STRATEGY: Try all possible locations and methods
      
      // 1. Try deleting from Supabase (handle both cloudId and direct ID)
      if (!file.isLocal) {
        const supabaseId = file.cloudId || file.id;
        const isOrphanedFile = /^\d+$/.test(file.name);
        
        try {
          if (file.type === 'floorplan') {
            await deleteFloorPlanFromSupabase(supabaseId);
          } else if (file.type === 'retrofit-survey') {
            await deleteRetrofitSurveyFromSupabase(supabaseId);
          } else {
            await deleteInventoryFromSupabase(supabaseId);
          }
          deleteSuccess = true;
          console.log(`[Delete] ✅ Successfully deleted ${file.name} from Supabase using ID: ${supabaseId}`);
          
          // For orphaned files, also delete ALL other records with the same name
          if (isOrphanedFile) {
            console.log(`[Delete] 🧹 Orphaned file detected - cleaning up ALL duplicates with name "${file.name}"`);
            try {
              // Get all files to find other records with same name
              const [allFloorPlans, allInventories] = await Promise.all([
                getAllFloorPlansFromSupabase(),
                getAllInventoriesFromSupabase()
              ]);
              
              const allFiles = file.type === 'floorplan' ? allFloorPlans : allInventories;
              const duplicates = allFiles.filter(f => f.name === file.name && f.id !== supabaseId);
              
              console.log(`[Delete] 🔍 Found ${duplicates.length} additional duplicates to clean up`);
              
              // Delete all duplicates
              for (const duplicate of duplicates) {
                try {
                  if (file.type === 'floorplan') {
                    await deleteFloorPlanFromSupabase(duplicate.id);
                  } else {
                    await deleteInventoryFromSupabase(duplicate.id);
                  }
                  console.log(`[Delete] ✅ Cleaned up duplicate: ID=${duplicate.id}, Name="${duplicate.name}"`);
                } catch (dupError) {
                  console.warn(`[Delete] ⚠️ Failed to delete duplicate ${duplicate.id}:`, dupError);
                }
              }
              
              console.log(`[Delete] 🧹 Cleanup complete! Removed ${duplicates.length + 1} total records named "${file.name}"`);
            } catch (cleanupError) {
              console.warn(`[Delete] ⚠️ Error during duplicate cleanup:`, cleanupError);
              // Don't fail the deletion if cleanup fails
            }
          }
        } catch (supabaseError) {
          console.warn(`[Delete] ❌ Supabase deletion failed for ID ${supabaseId}:`, supabaseError);
          errors.push(`Cloud storage: ${(supabaseError as Error).message}`);
          
          // For numeric-only names, this might be expected (orphaned records)
          if (isOrphanedFile) {
            console.log(`[Delete] 📝 File ${file.name} appears to be an orphaned database record - this is expected`);
            deleteSuccess = true; // Consider this a successful cleanup of orphaned record
          }
        }
      }

      // 2. Try deleting from local filesystem with comprehensive path checking
      const directory = file.type === 'floorplan' ? 'floorplans' : 'inventories';
      const possiblePaths = [
        // Standard paths
        `${directory}/${file.id}`,
        `${directory}/${file.id}.json`,
        `${directory}/${file.name}`,
        `${directory}/${file.name}.json`,
        // Handle cases where name and ID are different
        ...(file.name !== file.id ? [
          `${directory}/${file.name}`,
          `${directory}/${file.name}.json`
        ] : []),
        // Handle legacy naming patterns
        `${directory}/${file.name.replace(/\s+/g, '_')}.json`,
        `${directory}/${file.name.replace(/\s+/g, '-')}.json`,
      ];

      // Remove duplicates
      const uniquePaths = [...new Set(possiblePaths)];
      
      for (const path of uniquePaths) {
        try {
          await Filesystem.deleteFile({
            path,
            directory: Directory.Documents,
          });
          deleteSuccess = true;
          console.log(`[Delete] ✅ Successfully deleted ${file.name} from local storage at: ${path}`);
          break; // Stop after first successful deletion
        } catch (localError) {
          console.debug(`[Delete] 📁 Path ${path} not found or failed to delete:`, localError);
        }
      }

      // 3. Try cleaning up offline storage references
      try {
        const offlineFiles = offlineService.getOfflineFiles();
        console.log(`[Delete] 🔍 Checking ${offlineFiles.length} offline files for matches with:`, {
          id: file.id,
          name: file.name,
          cloudId: file.cloudId
        });
        
        const offlineFile = offlineFiles.find(of => 
          of.id === file.id || 
          of.cloudId === file.id || 
          of.name === file.name ||
          of.cloudId === file.cloudId
        );
        
        console.log(`[Delete] 🔍 Offline file search result:`, offlineFile || 'No match found');
        
        if (offlineFile) {
          console.log(`[Delete] 📱 Found offline storage reference for ${file.name}, removing...`);
          // Actually remove the offline file by setting availability to false
          await offlineService.toggleOfflineAvailability(offlineFile.id, false);
          deleteSuccess = true;
          console.log(`[Delete] ✅ Successfully removed ${file.name} from offline storage`);
        }
      } catch (offlineError) {
        console.warn(`[Delete] ❌ Failed to clean offline storage:`, offlineError);
        errors.push(`Offline storage: ${(offlineError as Error).message}`);
      }

      // 4. Special handling for orphaned/numeric files
      if (!deleteSuccess && /^\d+$/.test(file.name)) {
        console.log(`[Delete] 🔧 Handling orphaned numeric file: ${file.name}`);
        // These are likely orphaned database records - mark as successfully cleaned up
        deleteSuccess = true;
        console.log(`[Delete] ✅ Orphaned record ${file.name} marked for cleanup`);
      }

      // 5. Final validation - check if file still exists after deletion attempts
      if (deleteSuccess) {
        console.log(`[Delete] ✅ File "${file.name}" successfully removed from storage`);
        
        // Immediately remove from UI state for instant feedback
        setAllFiles(prevFiles => prevFiles.filter(f => f.id !== file.id));
        
        if (!skipConfirmation) {
          alert(`File "${displayName}" deleted successfully.`);
        }
        
        // Reload the file list to confirm deletion and sync with remote
        console.log(`[Delete] 🔄 Refreshing file list after deletion...`);
        await loadFiles();
        console.log(`[Delete] ✅ File list refreshed successfully`);
      } else {
        console.error(`[Delete] ❌ Failed to delete file "${file.name}" from any location. Errors:`, errors);
        
        // Provide helpful guidance based on file type
        let helpText = '';
        if (/^\d+$/.test(file.name)) {
          helpText = '\n\nThis appears to be an orphaned database record. It may already be removed from storage but still showing in the list.';
        } else if (errors.some(e => e.includes('not found'))) {
          helpText = '\n\nThe file may have already been deleted or moved. Try refreshing the page.';
        }
        
        const errorMessage = errors.length > 0 
          ? `Failed to delete "${displayName}".${helpText}\n\nTechnical details:\n${errors.join('\n')}`
          : `Failed to delete "${displayName}". The file may no longer exist.${helpText}`;
        
        alert(errorMessage);
        
        // Still reload the list in case the file was actually deleted but we couldn't confirm it
        await loadFiles();
      }
    } catch (error) {
      console.error(`[Delete] 💥 Unexpected error deleting file "${file.name}":`, error);
      alert(`Failed to delete "${displayName}": ${(error as Error).message}`);
      
      // Reload the list anyway in case partial deletion occurred
      await loadFiles();
    }
  };

  const handleDuplicateFile = async (file: FileItem) => {
    // Support floor plans, retrofit surveys, and inventories
    if (file.type !== 'floorplan' && file.type !== 'retrofit-survey' && file.type !== 'inventory') {
      alert('Only floor plans, retrofit surveys, and inventory reports can be duplicated at this time.');
      return;
    }

    // For inventory reports, copy immediately without prompts
    if (file.type === 'inventory') {
      try {
        console.log(`📋 [Copy] Starting inventory report copy: ${file.name}`);
        
        // Load original inventory data
        const { loadInventoryFromSupabase, saveInventoryToSupabase } = await import('../utils/supabaseStorage');
        const { loadPropertyInventoryFromFile } = await import('../utils/fileUtils');
        
        let originalInventory: any = null;
        
        if (file.isLocal) {
          originalInventory = await loadPropertyInventoryFromFile(file.id);
        } else {
          originalInventory = await loadInventoryFromSupabase(file.id);
        }
        
        if (!originalInventory) {
          alert('Failed to load original inventory data. The file may be corrupted or missing.');
          return;
        }
        
        // Generate new name with (Copy) suffix
        const suggestedName = `${formatFileNameForDisplay(file.name)} (Copy)`;
        
        // Create complete copy of all inventory data
        const copiedInventory = {
          ...originalInventory,
          propertyDetails: {
            ...originalInventory.propertyDetails,
            address: suggestedName,
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        console.log(`📋 [Copy] Copying inventory with all data:`, {
          hasRooms: !!copiedInventory.rooms?.length,
          hasCertificates: !!copiedInventory.certificates,
          hasKeys: !!copiedInventory.keysData?.length,
          sectionsCount: Object.keys(copiedInventory).length
        });
        
        // Save the copy
        const newInventoryId = await saveInventoryToSupabase(suggestedName, copiedInventory);
        
        console.log(`✅ [Copy] Inventory report copied successfully:`, newInventoryId);
        alert(`Inventory report copied successfully as "${suggestedName}"`);
        loadFiles(); // Refresh the file list
        
      } catch (error) {
        console.error('Error copying inventory report:', error);
        alert(`Failed to copy inventory report: ${(error as Error).message}`);
      }
      return;
    }

    // For retrofit surveys, copy immediately without address/orientation prompts
    if (file.type === 'retrofit-survey') {
      try {
        console.log(`📋 [Copy] Starting retrofit survey copy: ${file.name}`);
        
        // Load original survey data
        const originalSurvey = await loadRetrofitSurveyFromSupabase(file.id);
        
        if (!originalSurvey) {
          alert('Failed to load original survey data. The file may be corrupted or missing.');
          return;
        }
        
        // Generate new name with (Copy) suffix
        const suggestedName = `${formatFileNameForDisplay(file.name)} (Copy)`;
        
        // Create complete copy of all survey data
        const copiedSurvey: RetrofitSurveyData = {
          ...originalSurvey,
          name: suggestedName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          // Don't copy the ID - let Supabase generate a new one
          id: undefined
        };
        
        console.log(`📋 [Copy] Copying retrofit survey with all data:`, {
          hasFloorPlan: !!copiedSurvey.floor_plan,
          hasPhotos: Object.keys(copiedSurvey.property_front?.photos || {}).length > 0,
          hasCustomFields: !!copiedSurvey.custom_fields,
          sectionsCount: Object.keys(copiedSurvey).length
        });
        
        // Save the copy
        const newSurveyId = await saveRetrofitSurveyToSupabase(suggestedName, copiedSurvey);
        
        console.log(`✅ [Copy] Retrofit survey copied successfully:`, newSurveyId);
        alert(`Retrofit survey copied successfully as "${suggestedName}"`);
        loadFiles(); // Refresh the file list
        
      } catch (error) {
        console.error('Error copying retrofit survey:', error);
        alert(`Failed to copy retrofit survey: ${(error as Error).message}`);
      }
      return;
    }

    // For floor plans, show address/orientation prompts
    const suggestedName = `${formatFileNameForDisplay(file.name)} (Copy)`;
    
    setDuplicationState({
      isOpen: true,
      file,
      step: 'address',
      newAddress: suggestedName,
      newOrientation: 0,
      originalData: null
    });
  };

  const handleAddressSubmit = async (address: string) => {
    if (!duplicationState.file) return;

    try {
      // Load original data
      let originalData: SavedFloorPlanData | null = null;
      
      if (duplicationState.file.isLocal) {
        originalData = await loadFloorPlanFromFile(duplicationState.file.id);
      } else {
        originalData = await loadFloorPlanFromSupabase(duplicationState.file.id);
      }

      if (!originalData) {
        alert('Failed to load original floor plan data. The file may be corrupted or missing.');
        setDuplicationState(prev => ({ ...prev, isOpen: false }));
        return;
      }

      setDuplicationState(prev => ({
        ...prev,
        step: 'orientation',
        newAddress: address,
        originalData
      }));
    } catch (error) {
      console.error('Error loading floor plan for duplication:', error);
      alert(`Failed to load floor plan for duplication: ${(error as Error).message}`);
      setDuplicationState(prev => ({ ...prev, isOpen: false }));
    }
  };

  const handleOrientationSubmit = async (orientation: number) => {
    if (!duplicationState.originalData || !duplicationState.newAddress) return;

    setDuplicationState(prev => ({ ...prev, step: 'saving' }));

    try {
      // Type guard: this should only be called for floor plans
      const originalData = duplicationState.originalData as SavedFloorPlanData;
      
      // Create duplicate with new address and orientation - copy data as-is without migrations or fixes
      const duplicateData: SavedFloorPlanData = {
        ...originalData,
        propertyAddress: duplicationState.newAddress,
        propertyOrientation: orientation,
        name: duplicationState.newAddress,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Save the duplicate
      await saveFloorPlanToSupabase(duplicationState.newAddress, duplicateData);
      
      // Show simple success message
      alert(`Floor plan duplicated successfully as "${duplicationState.newAddress}"`);
      loadFiles(); // Refresh the file list
      
      setDuplicationState({
        isOpen: false,
        file: null,
        step: 'address',
        newAddress: '',
        newOrientation: 0,
        originalData: null
      });
    } catch (error) {
      console.error('Error saving duplicated floor plan:', error);
      alert(`Failed to duplicate floor plan: ${(error as Error).message}`);
      setDuplicationState(prev => ({ ...prev, step: 'orientation' }));
    }
  };

  const handleSkipAddress = () => {
    setDuplicationState(prev => ({ ...prev, isOpen: false }));
  };

  const handleSkipOrientation = () => {
    // Use default orientation of 0 (North)
    handleOrientationSubmit(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Full-screen loading overlay */}
      {isLoadingFiles && (
        <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
          <div className="bg-white/90 backdrop-blur-md rounded-3xl p-16 shadow-2xl border-2 border-white/50 max-w-md mx-4">
            <div className="text-center">
              <div className="relative mb-8">
                <div className="inline-block animate-spin rounded-full h-20 w-20 border-4 border-gray-200 border-t-blue-600"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Folder className="w-10 h-10 text-blue-600 animate-pulse" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Loading Your Files</h3>
              <p className="text-gray-600 text-lg">Please wait while we fetch your floor plans and reports...</p>
              <div className="mt-6">
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <header className="bg-gradient-to-r from-white via-slate-50/50 to-white backdrop-blur-sm shadow-xl sticky top-0 z-50 border-b border-slate-200/60">
        <div className="w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl shadow-lg">
              <Folder className="w-6 h-6 text-white drop-shadow-md" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900 bg-clip-text text-transparent">My Files</h1>
              <p className="text-sm text-slate-600 font-medium">
                {fileCounts.all} files • {fileCounts.cloud} cloud • {fileCounts.local} local
                {fileCounts.syncing > 0 && ` • ${fileCounts.syncing} syncing`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                setIsMultiSelectMode(!isMultiSelectMode);
                if (isMultiSelectMode) {
                  clearSelection();
                }
              }}
              className={`p-3 rounded-xl transition-all duration-200 border hover:shadow-md ${
                isMultiSelectMode 
                  ? 'bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100' 
                  : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600 border-gray-200 hover:border-blue-300'
              }`}
              title={isMultiSelectMode ? "Exit Multi-Select" : "Multi-Select Mode"}
            >
              <Check size={20} />
            </button>
            <button
              onClick={() => setIsCleanupModalOpen(true)}
              className="p-3 rounded-xl text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:shadow-md"
              title="File Cleanup Tool - Remove duplicates and orphaned files"
            >
              <FileX size={20} />
            </button>
            <button
              onClick={() => onNavigate({ page: 'start' })}
              className="p-3 rounded-xl text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 transition-all duration-200 border border-gray-200 hover:border-yellow-300 hover:shadow-md"
              title="Go to Home"
            >
              <Home size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="p-4 sm:p-6 space-y-6">
        {/* Enhanced Search and Filter Section */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by file name, address, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-4 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
          
          {/* Unified Filter */}
          <div className="mt-4 flex flex-col space-y-2">
            <span className="text-sm font-medium text-gray-700">Filter by Type:</span>
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-1 border border-blue-200/50 min-w-min">
              {[
                { key: 'all', label: 'All Files', count: fileCounts.all, color: 'from-slate-500 to-slate-600' },
                { key: 'energy', label: 'Energy Plans', count: fileCounts.energy, color: 'from-green-500 to-emerald-600' },
                { key: 'estate', label: 'Estate Plans', count: fileCounts.estate, color: 'from-blue-500 to-indigo-600' },
                { key: 'inventory', label: 'Inventory Reports', count: fileCounts.inventory, color: 'from-amber-500 to-orange-600' },
                { key: 'retrofit-survey', label: 'Retrofit Surveys', count: fileCounts['retrofit-survey'], color: 'from-purple-500 to-violet-600' }
              ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setFilterType(filter.key as typeof filterType)}
                    className={`group relative px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      filterType === filter.key
                        ? 'bg-white shadow-md scale-105'
                        : 'hover:bg-white/50'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${filter.color} ${
                        filterType === filter.key ? 'ring-2 ring-offset-1 ring-current' : ''
                      }`}></div>
                      <span className={filterType === filter.key ? 'text-gray-900' : 'text-gray-600'}>
                        {filter.label}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        filterType === filter.key 
                          ? 'bg-gradient-to-r ' + filter.color + ' text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {filter.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Team Member Filter (Admin Only) */}
          {isAdmin && teamMembers.length > 1 && (
            <div className="mt-4 flex flex-col space-y-2">
              <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Filter by Team Member:
              </span>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="w-full md:w-64 px-4 py-2.5 bg-white border-2 border-blue-200/70 rounded-xl text-sm font-medium text-gray-700 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm hover:shadow-md"
              >
                <option value="all">All Team Members ({allFiles.length} files)</option>
                {teamMembers.map((member) => {
                  const memberFileCount = allFiles.filter(f => f.userId === member.user_id).length;
                  const displayName = member.user_metadata?.first_name || 
                                     member.email?.split('@')[0] || 
                                     'Unknown';
                  return (
                    <option key={member.user_id} value={member.user_id}>
                      {displayName} ({memberFileCount} {memberFileCount === 1 ? 'file' : 'files'})
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          
          {(searchTerm || filterType !== 'all' || filterUser !== 'all') && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {filteredFiles.length} of {allFiles.length} files
                {filterType === 'energy' && ` • Energy Plans`}
                {filterType === 'estate' && ` • Estate Plans`}
                {filterType === 'inventory' && ` • Inventory Reports`}
                {filterType === 'retrofit-survey' && ` • Retrofit Surveys`}
              </p>
              {(searchTerm || filterType !== 'all' || filterUser !== 'all') && (
                <button
                  onClick={clearSearch}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Multi-Select Toolbar */}
        {isMultiSelectMode && (
          <div className="bg-blue-50/80 backdrop-blur-sm rounded-xl p-4 border border-blue-200 shadow-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">
                    {selectedFiles.size} of {filteredFiles.length} files selected
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={selectAllFiles}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearSelection}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {selectedFiles.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 size={16} />
                    <span>Delete ({selectedFiles.size})</span>
                  </button>
                )}
                <button
                  onClick={() => setIsMultiSelectMode(false)}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-white rounded-lg transition-colors"
                  title="Exit Multi-Select"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Network and Sync Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Network Status */}
          <div className={`bg-white/70 backdrop-blur-sm rounded-xl p-4 shadow-md border transition-all duration-200 ${
            networkStatus.isConnected ? 'border-green-200 bg-green-50/50' : 'border-orange-200 bg-orange-50/50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${networkStatus.isConnected ? 'bg-green-100' : 'bg-orange-100'}`}>
                  {networkStatus.isConnected ? (
                    <Wifi className="w-5 h-5 text-green-600" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-orange-600" />
                  )}
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Network: {networkStatus.isConnected ? 'Online' : 'Offline'}
                  </span>
                  <p className="text-xs text-gray-600">
                    {networkStatus.isConnected 
                      ? `Connected via ${networkStatus.connectionType}` 
                      : 'Working offline mode'
                    }
                  </p>
                </div>
              </div>
              {!networkStatus.isConnected && (
                <button
                  onClick={() => forceCheck()}
                  className="px-3 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <RefreshCw size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Cloud Storage Status */}
          <div className={`bg-white/70 backdrop-blur-sm rounded-xl p-4 shadow-md border transition-all duration-200 ${
            isCloudConnected ? 'border-blue-200 bg-blue-50/50' : 'border-red-200 bg-red-50/50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${isCloudConnected ? 'bg-blue-100' : 'bg-red-100'}`}>
                  {isCloudConnected ? (
                    <Cloud className="w-5 h-5 text-blue-600" />
                  ) : (
                    <HardDrive className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Cloud Storage: {isCloudConnected ? 'Connected' : 'Disconnected'}
                  </span>
                  <p className="text-xs text-gray-600">
                    {isCloudConnected 
                      ? 'Files sync automatically' 
                      : connectionError || 'Using offline storage'
                    }
                  </p>
                </div>
              </div>
              {!isCloudConnected && (
                <button
                  onClick={loadFiles}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sync Status */}
        {syncStatus.status !== 'idle' && (
          <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 shadow-md border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <RefreshCw className={`w-5 h-5 text-blue-600 ${syncStatus.status === 'syncing' ? 'animate-spin' : ''}`} />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Sync Status: {syncStatus.status === 'syncing' ? 'Syncing' : 'Pending'}
                  </span>
                  <p className="text-xs text-gray-600">
                    {syncStatus.status === 'syncing' 
                      ? `${syncStatus.progress}/${syncStatus.total} files synced` 
                      : `${syncStatus.total} files waiting to sync`
                    }
                  </p>
                </div>
              </div>
              {syncStatus.total > 0 && (
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(syncStatus.progress / syncStatus.total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="bg-yellow-50/80 backdrop-blur-sm border border-yellow-200 rounded-xl p-4 shadow-md">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-yellow-800">Connection Issue</h4>
                <p className="text-sm text-yellow-700 mt-1">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {allFiles.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-12 shadow-lg border border-white/50">
            <div className="text-center">
              <div className="mx-auto w-24 h-24 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center mb-6">
                <Folder size={48} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">No Files Yet</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Start your journey by creating your first floor plan or inventory report. Your files will appear here once created.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => onNavigate({ page: 'floorPlan' })}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2"
                >
                  <LayoutGrid size={20} />
                  <span>Create Floor Plan</span>
                </button>
                <button
                  onClick={() => onNavigate({ page: 'propertyInventory' })}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2"
                >
                  <FileText size={20} />
                  <span>Create Report</span>
                </button>
              </div>
            </div>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-12 shadow-lg border border-white/50">
            <div className="text-center">
              <div className="mx-auto w-24 h-24 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mb-6">
                <Search size={48} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">No Matching Files</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                We couldn't find any files matching your search criteria. Try adjusting your filters or search terms.
              </p>
              <button 
                onClick={clearSearch}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear Search & Filters
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFiles.map((file) => {
              const FileIcon = getFileTypeIcon(file.type);
              const isOrphanedFile = /^\d+$/.test(file.name);
              const isProblematicFile = isOrphanedFile || file.syncStatus === 'local-only';
              
              return (
                <div 
                  key={`${file.id}-${file.isLocal ? 'local' : 'cloud'}`} 
                  className={`group backdrop-blur-sm rounded-2xl p-6 shadow-lg border transition-all duration-300 ${
                    isMultiSelectMode ? 'cursor-default' : 'cursor-pointer'
                  } ${
                    selectedFiles.has(file.id) 
                      ? 'bg-gradient-to-br from-blue-50 via-white to-blue-50 border-blue-300 shadow-2xl scale-[1.02]'
                      : isProblematicFile 
                        ? 'bg-gradient-to-br from-red-50 via-white to-red-50 border-red-300 hover:shadow-2xl hover:scale-[1.02] hover:border-red-400' 
                        : 'bg-gradient-to-br from-white via-slate-50/30 to-white border-slate-200/60 hover:shadow-2xl hover:scale-[1.02] hover:border-slate-300'
                  }`}
                  onClick={() => {
                    if (isMultiSelectMode) {
                      toggleFileSelection(file.id);
                    } else {
                      handleOpenFile(file);
                    }
                  }}
                >
                  {/* File Header */}
                  <div className="flex items-start justify-between mb-4">
                    {/* Multi-select checkbox */}
                    {isMultiSelectMode && (
                      <div className="flex items-center mr-3">
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(file.id)}
                          onChange={() => toggleFileSelection(file.id)}
                          className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    <div className={`p-3 rounded-xl ${
                      isProblematicFile
                        ? 'bg-gradient-to-r from-red-400 to-orange-500'
                        : file.type === 'floorplan' 
                          ? file.planType === 'energy'
                            ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                            : file.planType === 'estate'
                              ? 'bg-gradient-to-r from-blue-400 to-indigo-500'
                              : file.planType === 'retrofit'
                                ? 'bg-gradient-to-r from-purple-400 to-violet-500'
                                : file.planType === 'inventory'
                                  ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                                  : 'bg-gradient-to-r from-green-400 to-blue-500' // fallback for legacy files
                          : 'bg-gradient-to-r from-blue-400 to-purple-500'
                    }`}>
                      <FileIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Orphaned File Warning */}
                      {isOrphanedFile && (
                        <div className="p-1.5 bg-red-100 rounded-lg" title="Orphaned file - Safe to delete">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      {/* Sync Status Indicator */}
                      {file.syncStatus === 'pending' && (
                        <div className="p-1.5 bg-orange-100 rounded-lg" title="Pending sync">
                          <RefreshCw className="w-4 h-4 text-orange-600 animate-spin" />
                        </div>
                      )}
                      {file.syncStatus === 'conflict' && (
                        <div className="p-1.5 bg-red-100 rounded-lg" title="Sync conflict">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      {file.syncStatus === 'synced' && !file.isLocal && (
                        <div className="p-1.5 bg-green-100 rounded-lg" title="Synced">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                      
                      {/* Storage Type Indicator */}
                      {file.isLocal ? (
                        <div className="p-1.5 bg-gray-100 rounded-lg" title="Stored locally">
                          <HardDrive className="w-4 h-4 text-gray-600" />
                        </div>
                      ) : (
                        <div className="p-1.5 bg-blue-100 rounded-lg" title="Stored in cloud">
                          <Cloud className="w-4 h-4 text-blue-600" />
                        </div>
                      )}

                      {/* Offline Availability Indicator */}
                      {file.isAvailableOffline && (
                        <div className="p-1.5 bg-green-100 rounded-lg" title="Available offline">
                          <Download className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="mb-4">
                    <h3 
                      className={`text-lg font-bold break-words line-clamp-2 mb-2 transition-colors ${
                        isOrphanedFile 
                          ? 'text-red-700 group-hover:text-red-800' 
                          : 'text-gray-900 group-hover:text-blue-600'
                      }`}
                      title={formatFileNameForDisplay(file.name)}
                    >
                      {formatFileNameForDisplay(file.name)}
                      {isOrphanedFile && (
                        <span className="text-xs text-red-600 font-normal ml-2">(Orphaned)</span>
                      )}
                    </h3>
                    
                    {/* Display address for retrofit surveys */}
                    {file.type === 'retrofit-survey' && file.address && (
                      <p className="text-sm text-gray-600 mb-2 flex items-center">
                        <Home className="w-4 h-4 mr-1" />
                        {file.address}
                      </p>
                    )}
                    
                    <div className="flex items-center flex-wrap gap-2 mb-3">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        isOrphanedFile
                          ? 'bg-red-100 text-red-700'
                          : file.type === 'floorplan' 
                            ? 'bg-green-100 text-green-700'
                            : file.type === 'inventory'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-purple-100 text-purple-700'
                      }`}>
                        {isOrphanedFile 
                          ? 'Orphaned Record' 
                          : file.type === 'floorplan' 
                            ? 'Floor Plan'
                            : file.type === 'inventory'
                              ? 'Inventory Report'
                              : 'Retrofit Survey'
                        }
                      </span>
                      
                      {/* Plan Type Badge (only for floor plans with planType) */}
                      {file.type === 'floorplan' && file.planType && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-gradient-to-r ${
                          file.planType === 'estate'
                            ? 'from-blue-500 to-indigo-600 text-white'
                            : 'from-green-500 to-emerald-600 text-white'
                        } shadow-sm`}>
                          {file.planType === 'estate' ? '🏢 Estate' : '⚡ Energy'}
                        </span>
                      )}
                      
                      {isOrphanedFile && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                          Safe to delete
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      {file.updatedAt && (
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{getRelativeTime(file.updatedAt)}</span>
                        </div>
                      )}
                      
                      {/* File Expiration Countdown */}
                      {(() => {
                        const expiration = getFileExpirationInfo(file.createdAt);
                        if (!expiration) return null;
                        
                        const colorClass = getExpirationColorClass(expiration);
                        const iconColor = getExpirationIconColor(expiration);
                        const progressColor = getProgressBarColor(expiration.percentageRemaining);
                        
                        return (
                          <div className="space-y-1.5">
                            <div className={`flex items-center space-x-2 text-sm font-medium ${colorClass} px-3 py-2 rounded-lg`}>
                              {expiration.isExpired ? (
                                <AlertCircle className={`w-4 h-4 ${iconColor}`} />
                              ) : (
                                <Timer className={`w-4 h-4 ${iconColor}`} />
                              )}
                              <span>
                                {expiration.isExpired ? (
                                  'Expired - Will be deleted'
                                ) : expiration.isCritical ? (
                                  <>⚠️ Expires in {formatTimeRemaining(expiration.daysRemaining)}</>
                                ) : expiration.isNearExpiration ? (
                                  <>⏰ Expires in {formatTimeRemaining(expiration.daysRemaining)}</>
                                ) : (
                                  <>Expires in {formatTimeRemaining(expiration.daysRemaining)}</>
                                )}
                              </span>
                            </div>
                            
                            {/* Progress bar */}
                            {!expiration.isExpired && (
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full transition-all duration-300 ${progressColor}`}
                                  style={{ width: `${expiration.percentageRemaining}%` }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Sync Status Info */}
                      {file.syncStatus && file.syncStatus !== 'synced' && (
                        <div className="flex items-center space-x-2 text-sm">
                          {file.syncStatus === 'pending' && (
                            <span className="text-orange-600 font-medium">⏳ Pending sync</span>
                          )}
                          {file.syncStatus === 'conflict' && (
                            <span className="text-red-600 font-medium">⚠️ Sync conflict</span>
                          )}
                          {file.syncStatus === 'local-only' && !isOrphanedFile && (
                            <span className="text-gray-600 font-medium">📱 Local only</span>
                          )}
                          {isOrphanedFile && (
                            <span className="text-red-600 font-medium">🗑️ Orphaned - Click delete to remove</span>
                          )}
                        </div>
                      )}
                      
                      {/* Last Synced Info */}
                      {file.lastSyncedAt && (
                        <div className="flex items-center space-x-2 text-xs text-gray-400">
                          <span>Last synced: {getRelativeTime(file.lastSyncedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    {/* Primary Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {!isOrphanedFile ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenFile(file);
                              }}
                              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                              title="Open and Edit"
                            >
                              <Edit3 size={16} />
                              <span>Open</span>
                            </button>
                            
                            {(file.type === 'floorplan' || file.type === 'retrofit-survey' || file.type === 'inventory') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateFile(file);
                                }}
                                className="p-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 rounded-xl transition-all duration-200 hover:scale-110 shadow-md hover:shadow-lg"
                                title={
                                  file.type === 'retrofit-survey' ? 'Copy Retrofit Survey' : 
                                  file.type === 'inventory' ? 'Copy Inventory Report' :
                                  'Duplicate Floor Plan'
                                }
                              >
                                <Copy size={16} />
                              </button>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center space-x-2 text-sm text-red-600 font-medium">
                            <AlertTriangle size={16} />
                            <span>Cannot open orphaned file</span>
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log(`[DEBUG] Delete button clicked for file:`, {
                            id: file.id,
                            name: file.name,
                            isOrphanedFile,
                            isLocal: file.isLocal,
                            cloudId: file.cloudId
                          });
                          handleDeleteFile(file);
                        }}
                        className={`flex items-center space-x-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg ${
                          isOrphanedFile
                            ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 hover:scale-105'
                            : 'p-2.5 bg-red-50 text-red-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-rose-600 hover:scale-110'
                        }`}
                        title={isOrphanedFile ? "Remove orphaned file" : "Delete File"}
                      >
                        <Trash2 size={16} />
                        {isOrphanedFile && <span>Remove</span>}
                      </button>
                    </div>

                    {/* Offline and Sync Actions */}
                    {!file.isLocal && (
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-3">
                          {/* Offline Toggle */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleOfflineAvailability(file, !file.isAvailableOffline);
                            }}
                            className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-colors ${
                              file.isAvailableOffline
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title={file.isAvailableOffline ? "Remove from offline" : "Make available offline"}
                          >
                            <Download size={14} />
                            <span>{file.isAvailableOffline ? 'Remove offline' : 'Make offline'}</span>
                          </button>

                          {/* Force Sync Button */}
                          {(file.syncStatus === 'pending' || file.syncStatus === 'conflict') && networkStatus.isConnected && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleForceSync(file);
                              }}
                              className="flex items-center space-x-2 px-3 py-1.5 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition-colors"
                              title="Force sync now"
                            >
                              <RefreshCw size={14} />
                              <span>Sync</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      
       <footer className="mt-12 bg-white/50 backdrop-blur-sm rounded-t-2xl border-t border-gray-200 p-6">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Cloud className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Secure Cloud Storage</span>
          </div>
          <p className="text-xs text-gray-500 max-w-md mx-auto">
            Your files are encrypted and synchronized across all your devices. Local copies are maintained as backup when offline.
          </p>
        </div>
      </footer>

      {/* Duplication Modals */}
      {duplicationState.isOpen && duplicationState.step === 'address' && (
        <AddressInputModal
          isOpen={true}
          onAddressSubmit={handleAddressSubmit}
          onSkip={handleSkipAddress}
          title="Duplicate Floor Plan"
          description="Enter a new address for the duplicated floor plan."
          allowSkip={true}
          initialAddress={duplicationState.newAddress}
        />
      )}
      
      {duplicationState.isOpen && duplicationState.step === 'orientation' && (
        <OrientationModal
          isOpen={true}
          onOrientationSubmit={handleOrientationSubmit}
          onSkip={handleSkipOrientation}
          title="Set Orientation for Duplicate"
          description="Set the orientation for the duplicated floor plan."
          allowSkip={true}
          initialOrientation={duplicationState.newOrientation}
        />
      )}
      
      {duplicationState.isOpen && duplicationState.step === 'saving' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-700">Duplicating floor plan...</p>
          </div>
        </div>
      )}

      {/* File Opening Loading Overlay */}
      {isOpeningFile && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/95 to-indigo-900/95 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center max-w-md w-full">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 mb-6 animate-pulse">
              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Opening File</h3>
            <p className="text-slate-600 text-sm">Please wait while we load your file...</p>
            <div className="mt-6 w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-[shimmer_1.5s_ease-in-out_infinite]" style={{ width: '60%' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* File Cleanup Modal */}
      <FileCleanupModal
        isOpen={isCleanupModalOpen}
        onClose={() => setIsCleanupModalOpen(false)}
        onCleanupComplete={() => {
          setIsCleanupModalOpen(false);
          loadFiles(); // Refresh the file list after cleanup
        }}
      />
    </div>
  );
};

export default MyFilesPage; 